import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const segments = [
  {
    name: "College Coaches",
    revenue: 125000,
    deals: 45,
    conversionRate: 18.5,
    avgDealSize: 2780,
    color: "bg-blue-500",
  },
  {
    name: "Partners",
    revenue: 98000,
    deals: 12,
    conversionRate: 35.2,
    avgDealSize: 8167,
    color: "bg-green-500",
  },
  {
    name: "Affiliates",
    revenue: 67000,
    deals: 89,
    conversionRate: 12.3,
    avgDealSize: 753,
    color: "bg-purple-500",
  },
  {
    name: "Teams",
    revenue: 156000,
    deals: 23,
    conversionRate: 28.1,
    avgDealSize: 6783,
    color: "bg-orange-500",
  },
  {
    name: "Advertisers",
    revenue: 234000,
    deals: 8,
    conversionRate: 45.6,
    avgDealSize: 29250,
    color: "bg-red-500",
  },
];

export const SegmentAnalysis: React.FC = () => {
  const totalRevenue = segments.reduce((sum, segment) => sum + segment.revenue, 0);

  return (
    <div className="space-y-6">
      {/* Segment Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {segments.map((segment) => (
          <Card key={segment.name}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-sm">{segment.name}</h3>
                <div className={`w-3 h-3 rounded-full ${segment.color}`} />
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-2xl font-bold">${segment.revenue.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">
                    {Math.round((segment.revenue / totalRevenue) * 100)}% of total
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="font-medium">{segment.deals}</p>
                    <p className="text-gray-500">Deals</p>
                  </div>
                  <div>
                    <p className="font-medium">{segment.conversionRate}%</p>
                    <p className="text-gray-500">Conv. Rate</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Segment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {segments
                .sort((a, b) => b.revenue - a.revenue)
                .map((segment) => {
                  const percentage = (segment.revenue / totalRevenue) * 100;
                  return (
                    <div key={segment.name}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">{segment.name}</span>
                        <Badge variant="outline">
                          ${segment.revenue.toLocaleString()}
                        </Badge>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${segment.color}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {percentage.toFixed(1)}% of total revenue
                      </p>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {segments
                .sort((a, b) => b.conversionRate - a.conversionRate)
                .map((segment) => (
                  <div key={segment.name} className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sm">{segment.name}</p>
                      <p className="text-xs text-gray-500">
                        Avg Deal: ${segment.avgDealSize.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{segment.conversionRate}%</p>
                      <p className="text-xs text-gray-500">Conversion</p>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Segment Trends */}
      <Card>
        <CardHeader>
          <CardTitle>Segment Trends & Insights</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center text-gray-500">
          Segment trend analysis and insights will be implemented here
        </CardContent>
      </Card>
    </div>
  );
};